import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Select,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiSearch, FiMail, FiCheckCircle, FiXCircle, FiEdit } from 'react-icons/fi';
import {
  getAllNewsletterSubscribers,
  updateNewsletterSubscriberStatus,
  NewsletterSubscriber,
  STATUS_COLORS,
} from '../../services/crmService';

// Cast icons as React.ElementType
const FiSearchIcon = FiSearch as React.ElementType;
const FiMailIcon = FiMail as React.ElementType;
const FiCheckCircleIcon = FiCheckCircle as React.ElementType;
const FiXCircleIcon = FiXCircle as React.ElementType;
const FiEditIcon = FiEdit as React.ElementType;

interface Props {
  onDataChange: () => void;
}

const NewsletterSubscribersTab: React.FC<Props> = ({ onDataChange }) => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSubscriber, setSelectedSubscriber] = useState<NewsletterSubscriber | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const data = await getAllNewsletterSubscribers();
      setSubscribers(data);
    } catch (error) {
      console.error('Error fetching newsletter subscribers:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch newsletter subscribers',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (subscriberId: string, newStatus: NewsletterSubscriber['status']) => {
    try {
      await updateNewsletterSubscriberStatus(subscriberId, newStatus);
      toast({
        title: 'Status updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchSubscribers();
      onDataChange();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Filter subscribers
  const filteredSubscribers = subscribers.filter((subscriber) => {
    const matchesSearch =
      !searchTerm ||
      subscriber.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (subscriber.source && subscriber.source.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = !statusFilter || subscriber.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const activeCount = subscribers.filter((s) => s.status === 'active').length;
  const unsubscribedCount = subscribers.filter((s) => s.status === 'unsubscribed').length;

  return (
    <Box>
      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Subscribers</StatLabel>
          <StatNumber color="blue.600">{subscribers.length}</StatNumber>
        </Stat>
        <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
          <StatLabel>Active</StatLabel>
          <StatNumber color="green.600">{activeCount}</StatNumber>
        </Stat>
        <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
          <StatLabel>Unsubscribed</StatLabel>
          <StatNumber color="orange.600">{unsubscribedCount}</StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Main Content */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        {/* Filters */}
        <HStack mb={4} spacing={4}>
          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none">
              <FiSearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search by email or source..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Select
            placeholder="All Statuses"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            w="200px"
          >
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
          </Select>
        </HStack>

        {/* Table */}
        {filteredSubscribers.length > 0 ? (
          <Box overflowX="auto" overflowY="auto" maxH="600px" border="1px solid" borderColor="gray.200" borderRadius="md">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th>Source</Th>
                  <Th>Subscribed Date</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSubscribers.map((subscriber) => (
                  <Tr key={subscriber.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <HStack>
                        <Text fontSize="sm" fontWeight="medium">{subscriber.email}</Text>
                        <IconButton
                          aria-label="Email"
                          icon={React.createElement(FiMailIcon)}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          as="a"
                          href={`mailto:${subscriber.email}`}
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS.newsletter[subscriber.status]}>
                        {subscriber.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge variant="outline" fontSize="xs">
                        {subscriber.source || 'website'}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{formatDate(subscriber.createdAt)}</Td>
                    <Td>
                      <HStack spacing={1}>
                        {subscriber.status === 'unsubscribed' ? (
                          <Tooltip label="Resubscribe">
                            <IconButton
                              aria-label="Resubscribe"
                              icon={React.createElement(FiCheckCircleIcon)}
                              size="sm"
                              variant="ghost"
                              colorScheme="green"
                              onClick={() => handleStatusUpdate(subscriber.id, 'active')}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip label="Unsubscribe">
                            <IconButton
                              aria-label="Unsubscribe"
                              icon={React.createElement(FiXCircleIcon)}
                              size="sm"
                              variant="ghost"
                              colorScheme="orange"
                              onClick={() => handleStatusUpdate(subscriber.id, 'unsubscribed')}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="View Details">
                          <IconButton
                            aria-label="View Details"
                            icon={React.createElement(FiEditIcon)}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => {
                              setSelectedSubscriber(subscriber);
                              onOpen();
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Text color="gray.500" textAlign="center" py={8}>
            {searchTerm || statusFilter
              ? 'No subscribers match your filters'
              : 'No newsletter subscribers found'}
          </Text>
        )}
      </Box>

      {/* Details Modal */}
      {selectedSubscriber && (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Subscriber Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Email:</Text>
                  <HStack>
                    <Text>{selectedSubscriber.email}</Text>
                    <Button
                      size="xs"
                      leftIcon={React.createElement(FiMailIcon)}
                      colorScheme="blue"
                      as="a"
                      href={`mailto:${selectedSubscriber.email}`}
                    >
                      Send Email
                    </Button>
                  </HStack>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Status:</Text>
                  <Badge colorScheme={STATUS_COLORS.newsletter[selectedSubscriber.status]} fontSize="md">
                    {selectedSubscriber.status}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Source:</Text>
                  <Badge variant="outline" fontSize="md">{selectedSubscriber.source || 'website'}</Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Subscribed Date:</Text>
                  <Text>{formatDate(selectedSubscriber.createdAt)}</Text>
                </Box>
                {selectedSubscriber.updatedAt && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Last Updated:</Text>
                    <Text fontSize="sm" color="gray.600">{formatDate(selectedSubscriber.updatedAt)}</Text>
                  </Box>
                )}
                <Box bg="blue.50" p={3} borderRadius="md" mt={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>Change Status:</Text>
                  <HStack>
                    {selectedSubscriber.status === 'unsubscribed' ? (
                      <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={React.createElement(FiCheckCircleIcon)}
                        onClick={() => handleStatusUpdate(selectedSubscriber.id, 'active')}
                      >
                        Resubscribe
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        colorScheme="orange"
                        leftIcon={React.createElement(FiXCircleIcon)}
                        onClick={() => handleStatusUpdate(selectedSubscriber.id, 'unsubscribed')}
                      >
                        Unsubscribe
                      </Button>
                    )}
                  </HStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default NewsletterSubscribersTab;
